// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMyOrganization } from "@/hooks/useMyOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Pencil, Copy, Archive, Plus, Package, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

const EMPTY = {
  name: "",
  description: "",
  price: 0,
  category: "",
  is_active: true,
  apply_to_all_events: false,
  level: "team",
  requires_team: false,
  requires_player: false,
  requires_game_date: false,
};

export default function ExtrasCatalogPage() {
  const { organization } = useMyOrganization();
  const orgId = organization?.id;

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "archived">("active");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<any | null>(null);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("extras_catalog")
      .select("*")
      .or(`organization_id.eq.${orgId},organization_id.is.null`)
      .order("created_at", { ascending: true });
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    setItems(data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filterStatus === "active" && !it.is_active) return false;
      if (filterStatus === "archived" && it.is_active) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (
          !(it.name ?? "").toLowerCase().includes(q) &&
          !(it.description ?? "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [items, filterStatus, search]);

  const openCreate = () => setEditing({ ...EMPTY, organization_id: orgId });

  const save = async () => {
    if (!editing?.name?.trim()) {
      toast.error("Item name is required");
      return;
    }
    const payload: any = {
      organization_id: editing.organization_id ?? orgId,
      name: editing.name,
      description: editing.description || null,
      price: Math.round(Number(editing.price) || 0),
      category: editing.category || null,
      is_active: editing.is_active ?? true,
      apply_to_all_events: editing.apply_to_all_events ?? false,
      level: (editing as any).level ?? "team",
      requires_team: editing.requires_team ?? false,
      requires_player: editing.requires_player ?? false,
      requires_game_date: editing.requires_game_date ?? false,
    };
    const res = editing.id
      ? await (supabase as any).from("extras_catalog").update(payload).eq("id", editing.id)
      : await (supabase as any).from("extras_catalog").insert(payload);
    if (res.error) {
      toast.error(res.error.message);
      return;
    }
    toast.success(editing.id ? "Catalog item updated" : "Catalog item created");
    setEditing(null);
    load();
  };

  const duplicate = async (it: any) => {
    const { error } = await (supabase as any).from("extras_catalog").insert({
      organization_id: it.organization_id ?? orgId,
      name: `${it.name} (Copy)`,
      description: it.description,
      price: it.price,
      category: it.category,
      is_active: true,
      apply_to_all_events: it.apply_to_all_events,
      level: it.level ?? "team",
      requires_team: it.requires_team,
      requires_player: it.requires_player,
      requires_game_date: it.requires_game_date,
    });
    if (error) return toast.error(error.message);
    toast.success("Duplicated");
    load();
  };

  const toggleActive = async (it: any, value: boolean) => {
    const { error } = await (supabase as any)
      .from("extras_catalog")
      .update({ is_active: value })
      .eq("id", it.id);
    if (error) return toast.error(error.message);
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, is_active: value } : x)));
  };

  const archive = async (it: any) => {
    if (!confirm(`Archive "${it.name}"?`)) return;
    await toggleActive(it, false);
  };

  const requiresBadges = (it: any) => {
    const tags: string[] = [];
    tags.push((it.level ?? "team") === "player" ? "Per player" : "Per team");
    if (it.requires_team) tags.push("Team");
    if (it.requires_player) tags.push("Player");
    if (it.requires_game_date) tags.push("Game date");
    return tags;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            Extras Catalog
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reusable add-ons (e.g. Video Package, T-Shirt) you can offer across events.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" /> Add Item
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="all">All</option>
          <option value="active">Active only</option>
          <option value="archived">Archived</option>
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items…"
            className="pl-8"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center">
          <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No catalog items found.</p>
          <Button className="mt-4" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" /> Create your first item
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <div
              key={it.id}
              className={`border border-border rounded-lg p-3 bg-card ${!it.is_active ? "opacity-60" : ""}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-foreground">{it.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ${(Number(it.price) / 100).toFixed(2)}
                    </span>
                    {it.category && (
                      <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border bg-secondary text-foreground border-border">
                        {it.category}
                      </span>
                    )}
                    {requiresBadges(it).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border bg-blue-100 text-blue-700 border-blue-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  {it.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{it.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <label className="flex items-center gap-1 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={it.is_active}
                      onChange={(e) => toggleActive(it, e.target.checked)}
                    />
                    Active
                  </label>
                  <button
                    onClick={() => setEditing(it)}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => duplicate(it)}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => archive(it)}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                    title="Archive"
                  >
                    <Archive className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Catalog Item" : "New Catalog Item"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Item name *</label>
                <Input
                  value={editing.name ?? ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g. Video Package"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Price (cents)</label>
                  <Input
                    type="number"
                    value={editing.price ?? ""}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    placeholder="1500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <Input
                    value={editing.category ?? ""}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    placeholder="e.g. Media"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={editing.description ?? ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Describe the item…"
                  rows={3}
                />
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="text-sm font-semibold text-foreground mb-2">Sold to</p>
                <div className="flex flex-wrap gap-4 mb-3">
                  {(["team", "player"] as const).map((lvl) => (
                    <label key={lvl} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        checked={((editing as any).level ?? "team") === lvl}
                        onChange={() =>
                          setEditing({
                            ...editing,
                            level: lvl,
                            ...(lvl === "team" ? { requires_player: false } : {}),
                          } as any)
                        }
                      />
                      {lvl === "team" ? "Team (quantity only)" : "Player"}
                    </label>
                  ))}
                </div>
                <p className="text-sm font-semibold text-foreground mb-2">Required info from buyer</p>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!editing.requires_team}
                      onChange={(e) => setEditing({ ...editing, requires_team: e.target.checked })}
                      className="rounded"
                    />
                    Team
                  </label>
                  <label
                    className={`flex items-center gap-2 text-sm ${
                      ((editing as any).level ?? "team") === "team" ? "opacity-40" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={((editing as any).level ?? "team") === "team"}
                      checked={
                        ((editing as any).level ?? "team") === "player" &&
                        !!editing.requires_player
                      }
                      onChange={(e) => setEditing({ ...editing, requires_player: e.target.checked })}
                      className="rounded"
                    />
                    Player
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!editing.requires_game_date}
                      onChange={(e) => setEditing({ ...editing, requires_game_date: e.target.checked })}
                      className="rounded"
                    />
                    Game date
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  e.g. a Video Package needs Team, Player, and Game date.
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!editing.apply_to_all_events}
                  onChange={(e) => setEditing({ ...editing, apply_to_all_events: e.target.checked })}
                  className="rounded"
                />
                Offer on all events automatically
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
